import { useState, useContext, useEffect } from "react";
import { useFormik, Form, FormikProvider } from "formik";
import * as Yup from "yup";
import {
  Grid,
  Stack,
  Dialog,
  Button,
  TextField,
  Typography,
  DialogContent,
  FormHelperText,
} from "@mui/material";
import { useMutation } from "@apollo/client";
import { pdfjs } from "react-pdf";
import { PiFilePdfDuotone } from "react-icons/pi";
// Project imports
import folderImage from "../../../Assets/pdf.png";
import "../../../Style/dialogstyle.scss";
import { AuthContext } from "../../../Context/AuthContext";
import {
  SelectStudentByClass,
  SelectExamTypeForForm,
  SelectSubjectByClassId,
} from "../../../Components/Include/DynamicSelect/DynamicSelect";
import { DialogTitleComp } from "../../../Utils/Component";
import { useDeleteImageServer } from "../../CropImage/DeleteImageServer";
import { CREATE_EXAM, UPDATE_EXAM } from "../../../Schema/ExamType";
import PdfViewer from "../../../Utils/PdfViewer";
import { uploadPdf } from "../../CropImage/UploadPdf";
import LoadingProgress from "../../../Utils/LoadingProgress";

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url
).toString();

export default function ExamForm({
  row,
  classId,
  setRefetch,
  open,
  editData,
  dialogTitle,
  handleClose,
}) {
  const { t, setAlert, language } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);

  // =================================== Upload Image ====================================

  const mutationDelete = useDeleteImageServer();
  const [profileHook, setProfileHook] = useState("");
  const [imageFile, setImageFile] = useState(null);

  const [statusProgress, setStatusProgress] = useState(false);
  const [progress, setProgress] = useState(10);

  const handleUploadFile = async (e) => {
    const imageFile = e.target.files[0];
    if (imageFile && imageFile.type === "application/pdf") {
      setImageFile(imageFile);
      const uploadedPdf = await uploadPdf(imageFile);
      // console.log("uploadedPdf::", uploadedPdf);
      setProfileHook(uploadedPdf);
      setStatusProgress(true);
      setProgress(10);
    } else {
      alert("Please upload a valid PDF file!");
    }
  };

  const handleCloseAction = () => {
    handleClose();
    if (profileHook) {
      // delete iamge from server ====================================================
      let splitSrc = profileHook?.split(":")[4];
      mutationDelete.mutate({
        storage: "sr_music_academy",
        folder: "sr_music_images",
        file: splitSrc.split("/")[0],
      });
    }
    setProfileHook("");
    setImageFile("");
  };

  const handleRemovePdf = () => {
    if (profileHook) {
      // delete iamge from server ====================================================
      let splitSrc = profileHook?.split(":")[4];
      // console.log("splitSrc::", splitSrc, splitSrc.split("/")[0]);
      mutationDelete.mutate({
        storage: "sr_music_academy",
        folder: "sr_music_images",
        file: splitSrc.split("/")[0],
      });
    }
    setProfileHook("");
    setImageFile("");
  };

  // ================================= End upload Image ===============================

  const [createExam] = useMutation(CREATE_EXAM, {
    onCompleted: ({ createExam }) => {
      setLoading(false);
      if (createExam?.isSuccess === true) {
        setAlert(true, "success", createExam?.message);
        handleClose();
        resetForm();
        setRefetch();
      } else {
        setAlert(true, "error", createExam?.message);
      }
    },
    onError: ({ message }) => {
      setLoading(false);
      setAlert(true, "error", { messageKh: message, messageEn: message });
    },
  });

  const [updateExam] = useMutation(UPDATE_EXAM, {
    onCompleted: ({ updateExam }) => {
      setLoading(false);
      if (updateExam?.isSuccess === true) {
        setAlert(true, "success", updateExam?.message);
        handleClose();
        setRefetch();
        // delete iamge from server ====================================================
        if (profileHook && editData?.file) {
          let splitSrc = editData?.file?.split(":")[4];
          mutationDelete.mutate({
            storage: "sr_music_academy",
            folder: "sr_music_images",
            file: splitSrc.split("/")[0],
          });
        }
        setProfileHook("");
        setImageFile("");
      } else {
        setAlert(true, "error", updateExam?.message);
      }
    },
    onError: ({ message }) => {
      setLoading(false);
      setAlert(true, "error", { messageKh: message, messageEn: message });
    },
  });

  const validationSchema = Yup.object().shape({
    studentId: Yup.object().shape({
      id:
        dialogTitle === "Create"
          ? Yup.string().required(t("required"))
          : Yup.string(),
      title:
        dialogTitle === "Create"
          ? Yup.string().required(t("required"))
          : Yup.string(),
    }),
    examTypeId: Yup.object().shape({
      id:
        dialogTitle === "Create"
          ? Yup.string().required(t("required"))
          : Yup.string(),
      title:
        dialogTitle === "Create"
          ? Yup.string().required(t("required"))
          : Yup.string(),
    }),
    subjectId: Yup.object().shape({
      id: Yup.string().required(t("required")),
      title: Yup.string().required(t("required")),
    }),
    exercise: Yup.string().required(t("required")),
    score: Yup.string(),
  });

  const formik = useFormik({
    initialValues: {
      studentId: { id: "", title: "" },
      examTypeId: { id: "", title: "" },
      subjectId: { id: "", title: "" },
      exercise: "",
      score: 0,
    },
    validationSchema,
    onSubmit: async (values) => {
      setLoading(true);
      const payload = {
        ...values,
        studentId: values?.studentId?.id,
        examTypeId: values?.examTypeId?.id,
        subjectId: values?.subjectId?.id,
        file: profileHook ? profileHook : editData?.file ? editData?.file : "",
      };

      // console.log("payload::", payload)

      if (dialogTitle === "Create") {
        createExam({ variables: { input: payload } });
      } else {
        updateExam({
          variables: { id: row?._id, arrayId: editData?._id, input: payload },
        });
      }
    },
  });

  const {
    values,
    errors,
    touched,
    resetForm,
    handleSubmit,
    setFieldValue,
    getFieldProps,
  } = formik;

  // console.log("editData::", editData);

  useEffect(() => {
    // console.log("hello");
    if (editData) {
      setFieldValue("studentId", {
        id: row?.studentId?._id,
        title: row?.studentId?.familyName + " " + row?.studentId?.studentName,
      });
      setFieldValue("examTypeId", {
        id: row?.examTypeId?._id,
        title: row?.examTypeId?.examName,
      });
      setFieldValue("subjectId", {
        id: editData?.subjectId?._id,
        title:
          language === "en"
            ? editData?.subjectId?.enName
            : editData?.subjectId?.khName,
      });
      setFieldValue("exercise", editData?.exercise);
      setFieldValue("score", editData?.score);
    }
  }, [open]);

  return (
    <Dialog open={open} className="dialog-container" maxWidth="sm" fullWidth>
      <DialogTitleComp
        title={
          dialogTitle === "Create"
            ? t("modal_title_create_exam")
            : t("modal_title_update_exam")
        }
        classTitle="dialog-title"
        classIcon="close-icon"
        handleClose={handleCloseAction}
      />

      <DialogContent dividers>
        <FormikProvider value={formik}>
          <Form noValidate autoComplete="off" onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              {dialogTitle === "Create" && (
                <Grid item xs={12} md={6}>
                  <Typography className={t("class-name-field-title")}>
                    {t("thead_student")}
                  </Typography>
                  <SelectStudentByClass
                    classId={classId}
                    value={values?.studentId}
                    onChange={(val) =>
                      setFieldValue("studentId", val || { id: "", title: "" })
                    }
                  />
                  {touched.studentId?.id && errors.studentId?.id && (
                    <FormHelperText error>
                      {errors.studentId?.id}
                    </FormHelperText>
                  )}
                </Grid>
              )}

              {dialogTitle === "Create" && (
                <Grid item xs={12} md={6}>
                  <SelectExamTypeForForm
                    classId={classId}
                    value={values?.examTypeId}
                    className={"text-field"}
                    onChange={(val) =>
                      setFieldValue("examTypeId", val || { id: "", title: "" })
                    }
                  />
                  {touched.examTypeId?.id && errors.examTypeId?.id && (
                    <FormHelperText error>
                      {errors.examTypeId?.id}
                    </FormHelperText>
                  )}
                </Grid>
              )}

              <Grid item xs={12} md={6}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_subject")}
                </Typography>
                <SelectSubjectByClassId
                  classId={classId}
                  value={values?.subjectId}
                  onChange={(val) =>
                    setFieldValue("subjectId", val || { id: "", title: "" })
                  }
                />
                {touched.subjectId?.id && errors.subjectId?.id && (
                  <FormHelperText error>{errors.subjectId?.id}</FormHelperText>
                )}
              </Grid>

              <Grid item xs={12} md={8}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_exercise")}
                </Typography>
                <TextField
                  className="text-field"
                  size="small"
                  multiline
                  fullWidth
                  placeholder={t("thead_exercise")}
                  {...getFieldProps("exercise")}
                  error={Boolean(touched.exercise && errors.exercise)}
                  helperText={touched.exercise && errors.exercise}
                />
              </Grid>

              <Grid item xs={4}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_score")}
                </Typography>
                <TextField
                  className="text-field"
                  size="small"
                  type="number"
                  fullWidth
                  placeholder={t("thead_score")}
                  {...getFieldProps("score")}
                  error={Boolean(touched.score && errors.score)}
                  helperText={touched.score && errors.score}
                />
              </Grid>

              <Grid item xs={12}>
                <Stack direction="row" justifyContent="center">
                  <Stack direction={"column"} spacing={2}>
                    {editData?.file ? (
                      <PdfViewer pdf={editData?.file} />
                    ) : imageFile ? (
                      <PdfViewer pdf={imageFile} />
                    ) : (
                      <img style={{ width: "240px" }} src={folderImage} />
                    )}
                    <Stack direction="row" justifyContent={"space-between"}>
                      <Button
                        component="label"
                        className="btn-upload"
                        endIcon={<PiFilePdfDuotone />}
                      >
                        <TextField
                          accept="application/pdf"
                          type="file"
                          id="image"
                          sx={{ display: "none" }}
                          onChange={handleUploadFile}
                        />
                        Upload
                        <input type="file" hidden />
                      </Button>
                      <Button
                        className="btn-remove"
                        onClick={handleRemovePdf}
                        disabled={profileHook ? false : true}
                      >
                        Remove
                      </Button>
                    </Stack>
                  </Stack>
                </Stack>

                {statusProgress && progress < 100 && (
                  <LoadingProgress
                    progress={progress}
                    setProgress={setProgress}
                  />
                )}
              </Grid>

              <Grid item xs={12}>
                <Button
                  fullWidth
                  type="submit"
                  className={t("class-btn-create")}
                  disabled={statusProgress && progress < 100 ? true : false}
                >
                  {loading
                    ? t("loading")
                    : dialogTitle === "Update"
                    ? t("update")
                    : t("create")}
                </Button>
              </Grid>
            </Grid>
          </Form>
        </FormikProvider>
      </DialogContent>
    </Dialog>
  );
}
